// Intégration dans un Formulaire Shadcn

// Indique que ce composant doit être rendu côté client
"use client";

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Progress,
} from "@repo/ui";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { api } from "@convex/_generated/api";
import {
  isBackendError,
  TemplateFormEnum,
  TypeEvalEnum,
  type FormTemplateProps,
  type PamiReponses,
} from "../../lib/types";
import { cn } from "@repo/utils";
import { useFormPageNavigation } from "../../hooks/useFormPageNavigation";
import { FormTransitionOverlay } from "../../components/FormTransitionOverlay";
import {
  PAMI_QUESTIONS_PER_PAGE,
  pamiInstructions,
  pamiInstructionsShort,
  pamiQuestions,
  type PamiStatement,
} from "./pami-data";
import { PamiItemCard, SortableItem } from "./sortable-item";

const schema = z.object(
  pamiQuestions.reduce(
    (acc, q) => {
      acc[q.name] = z.array(z.any()).length(3);
      return acc;
    },
    {} as Record<string, z.ZodType<any> | z.ZodTypeAny>,
  ),
);

type FormData = z.infer<typeof schema>;

export function ReorderForm({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const [questionsFinales, setQuestionsFinales] = useState<{
    langueMaternelle: boolean | null;
    bonnesConditions: boolean | null;
    reponsesSinceres: boolean | null;
  }>({
    langueMaternelle: null,
    bonnesConditions: null,
    reponsesSinceres: null,
  });

  const questionsPerPage = PAMI_QUESTIONS_PER_PAGE;
  const totalPages = Math.ceil(pamiQuestions.length / questionsPerPage);
  const totalPagesDisplay = totalPages + 1;

  // `handleSavePage` est une déclaration de fonction (hoistée) : elle référence
  // `saveResponses`, défini plus bas, mais n'est appelée qu'au clic.
  async function handleSavePage(data: FormData, page: number) {
    if (editable) await saveResponses(data, page);
  }

  const { currentPage, isNavigating, showTransitionOverlay, goToPage } =
    useFormPageNavigation<FormData>({
      // +1 : la dernière page (index totalPages) porte les questions finales.
      pageCount: totalPagesDisplay,
      pageActuelle: evaluation.donnees.pageActuelle,
      // La sauvegarde a lieu aussi depuis la page des questions finales : c'est
      // la page cible qui est persistée, sinon un retour arrière ne serait pas
      // repris à la reconnexion.
      save: handleSavePage,
      // Le scroll est porté par le conteneur du formulaire, pas par la fenêtre.
      scrollToTop: false,
    });

  const isFinalQuestionsPage = currentPage === totalPages;
  const startIndex = currentPage * questionsPerPage;
  const visibleQuestions = pamiQuestions.slice(
    startIndex,
    startIndex + questionsPerPage,
  );
  const questionsFinalesComplete =
    questionsFinales.langueMaternelle !== null &&
    questionsFinales.bonnesConditions !== null &&
    questionsFinales.reponsesSinceres !== null;

  const reponses = evaluation.donnees.reponses as PamiReponses;
  const baseDefaultValues = pamiQuestions.reduce(
    (acc, q) => {
      acc[q.name] = reponses[q.name]?.orderedItems || q.statements;
      return acc;
    },
    {} as Record<string, PamiStatement[]>,
  );

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: baseDefaultValues as FormData,
  });

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (form.formState.isDirty && !form.formState.isSubmitting) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [form.formState.isDirty, form.formState.isSubmitting]);

  // MouseSensor + TouchSensor plutôt que PointerSensor : le drag souris démarre
  // au 1er px, le drag tactile exige un appui long de 200 ms. Sans ce délai, le
  // touchstart sur le handle déclenche un drag et le scroll de page est mangé.
  // `tolerance: 5` annule l'appui long si le doigt bouge de plus de 5 px → geste
  // interprété comme un scroll, pas comme un drag.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 1 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Item en cours de drag, par question : alimente le DragOverlay, seule copie
  // visible sous le doigt (l'original reste estompé à sa place).
  const [activeDrag, setActiveDrag] = useState<{
    questionName: string;
    item: PamiStatement;
    index: number;
    total: number;
  } | null>(null);

  // Consigne complète : dépliée d'office sur la 1re question, repliée ensuite
  // (elle est identique pour les 41 questions).
  const [showFullInstructions, setShowFullInstructions] = useState(false);
  useEffect(() => {
    setShowFullInstructions(currentPage === 0);
  }, [currentPage]);

  // Confirmation d'un déplacement : carte flashée + annonce lecteur d'écran.
  const [lastMoved, setLastMoved] = useState<{
    questionName: string;
    itemId: string;
  } | null>(null);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!lastMoved) return;
    const timer = setTimeout(() => setLastMoved(null), 900);
    return () => clearTimeout(timer);
  }, [lastMoved]);

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const buildDonnees = (
    data: FormData,
    withQuestionsFinales = false,
    pageActuelle?: number,
  ) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.PAMI,
    reponses: Object.entries(data).reduce(
      (acc, [questionName, statements]) => {
        acc[questionName] = {
          name: questionName,
          label: "",
          orderedItems: statements.map(
            ({ id, label, prefixLabel }: PamiStatement) => ({
              id,
              label,
              prefixLabel,
            }),
          ),
        };
        return acc;
      },
      {} as Record<
        string,
        { name: string; label: string; orderedItems: PamiStatement[] }
      >,
    ),
    ...(pageActuelle !== undefined && { pageActuelle }),
    ...(withQuestionsFinales && {
      questionsFinales: {
        langueMaternelle: questionsFinales.langueMaternelle as boolean,
        bonnesConditions: questionsFinales.bonnesConditions as boolean,
        reponsesSinceres: questionsFinales.reponsesSinceres as boolean,
      },
    }),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data, true),
      });

      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    }
  };

  const saveResponses = async (data: FormData, pageActuelle: number) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data, false, pageActuelle),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };


  function handleDragStart(event: any, questionName: string) {
    const items = (form.getValues(questionName) || []) as PamiStatement[];
    const index = items.findIndex((item) => item.id === event.active.id);
    const item = items[index];
    if (!item) return;
    setActiveDrag({ questionName, item, index, total: items.length });
  }

  function handleDragEnd(
    event: any,
    questionName: string,
    items: PamiStatement[],
  ) {
    setActiveDrag(null);
    const { active, over } = event;
    // `over` est null si le doigt est relâché hors de toute zone de drop.
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(items, oldIndex, newIndex);

      // Mise à jour du formulaire
      form.setValue(questionName, newOrder, { shouldDirty: true });
    }
  }

  function moveItem(questionName: string, fromIndex: number, toIndex: number) {
    const items = form.getValues(questionName) as PamiStatement[];
    if (
      !items ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= items.length ||
      toIndex >= items.length ||
      fromIndex === toIndex
    ) {
      return;
    }
    form.setValue(questionName, arrayMove(items, fromIndex, toIndex), {
      shouldDirty: true,
    });

    const moved = items[fromIndex] as PamiStatement;
    setLastMoved({ questionName, itemId: moved.id });
    setAnnouncement(
      `« ${moved.label} » déplacé en position ${toIndex + 1} sur ${items.length}.`,
    );
  }



  return (
    <div className="h-[calc(100dvh-61.48px)] border-box flex flex-col p-3 sm:p-4 overflow-y-auto">
      <Form {...form}>
        <form
          className="flex flex-col gap-6 sm:gap-8 w-full max-w-3xl mx-auto py-4 sm:py-8"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {isFinalQuestionsPage
                  ? "Questions finales"
                  : `Question ${currentPage + 1} / ${totalPagesDisplay}`}
              </span>
            </div>
            <Progress
              value={((currentPage + 1) / totalPagesDisplay) * 100}
              className="h-1.5"
            />
          </div>

          {isFinalQuestionsPage ? (
            <div
              className={cn(
                "space-y-8 transition-opacity",
                isNavigating && "pointer-events-none opacity-50",
              )}
              aria-busy={isNavigating}
            >
              <p className="text-lg sm:text-xl font-medium leading-relaxed">
                Avant de valider, merci de répondre aux questions suivantes :
              </p>
              {[
                {
                  key: "langueMaternelle" as const,
                  label: "J'ai passé le test dans ma langue maternelle",
                },
                {
                  key: "bonnesConditions" as const,
                  label:
                    "J'ai passé ce questionnaire dans de bonnes conditions",
                },
                {
                  key: "reponsesSinceres" as const,
                  label: "J'ai répondu de manière sincère et honnête",
                },
              ].map(({ key, label }) => (
                <div key={key} className="space-y-3">
                  <p className="text-base font-medium">{label}</p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      className="flex-1 sm:flex-none h-11 sm:h-10 sm:min-w-24"
                      variant={
                        questionsFinales[key] === true ? "default" : "outline"
                      }
                      onClick={() =>
                        setQuestionsFinales((prev) => ({
                          ...prev,
                          [key]: true,
                        }))
                      }
                      disabled={!editable}
                    >
                      Oui
                    </Button>
                    <Button
                      type="button"
                      className="flex-1 sm:flex-none h-11 sm:h-10 sm:min-w-24"
                      variant={
                        questionsFinales[key] === false ? "default" : "outline"
                      }
                      onClick={() =>
                        setQuestionsFinales((prev) => ({
                          ...prev,
                          [key]: false,
                        }))
                      }
                      disabled={!editable}
                    >
                      Non
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={cn(
                "space-y-6 transition-opacity",
                isNavigating && "pointer-events-none opacity-50",
              )}
              aria-busy={isNavigating}
            >
              <div className="space-y-2">
                <p className="text-lg sm:text-xl font-medium leading-relaxed">
                  {pamiInstructionsShort}
                </p>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm text-muted-foreground"
                  aria-expanded={showFullInstructions}
                  onClick={() => setShowFullInstructions((prev) => !prev)}
                >
                  {showFullInstructions
                    ? "Masquer la consigne complète"
                    : "Voir la consigne complète"}
                </Button>
                {showFullInstructions && (
                  <p className="text-sm text-muted-foreground leading-relaxed border-l-2 border-border pl-3">
                    {pamiInstructions}
                  </p>
                )}
              </div>
              {visibleQuestions.map((question) => (
                <FormField
                  key={question.name}
                  control={form.control}
                  name={question.name}
                  render={() => (
                    <FormItem>
                      <FormControl>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={(event: any) =>
                            editable && handleDragStart(event, question.name)
                          }
                          onDragCancel={() => setActiveDrag(null)}
                          onDragEnd={(event: any) =>
                            editable &&
                            handleDragEnd(
                              event,
                              question.name,
                              form.getValues(question.name),
                            )
                          }
                        >
                          <SortableContext
                            items={form.getValues(question.name) || []}
                            strategy={verticalListSortingStrategy}
                          >
                            {/* Ancres de sens : les numéros 1/2/3 seuls
                                n'indiquent pas dans quel sens classer. */}
                            <p className="mt-1 mb-1 text-xs font-medium text-muted-foreground">
                              ↑ Me correspond le plus
                            </p>
                            <div className="space-y-2 sm:space-y-1">
                              {(
                                (form.getValues(question.name) ||
                                  []) as PamiStatement[]
                              ).map((item: PamiStatement, index, items) => (
                                <SortableItem
                                  key={item.id}
                                  id={item.id}
                                  label={item.label}
                                  index={index}
                                  total={items.length}
                                  editable={editable}
                                  highlighted={
                                    lastMoved?.questionName === question.name &&
                                    lastMoved.itemId === item.id
                                  }
                                  onMove={(from, to) =>
                                    moveItem(question.name, from, to)
                                  }
                                />
                              ))}
                            </div>
                            <p className="mt-1 text-xs font-medium text-muted-foreground">
                              ↓ Me correspond le moins
                            </p>
                          </SortableContext>

                          <DragOverlay dropAnimation={{ duration: 200 }}>
                            {activeDrag &&
                            activeDrag.questionName === question.name ? (
                              <PamiItemCard
                                label={activeDrag.item.label}
                                index={activeDrag.index}
                                total={activeDrag.total}
                                editable={editable ?? true}
                                state="overlay"
                              />
                            ) : null}
                          </DragOverlay>
                        </DndContext>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          )}

          {/* Annonce du nouvel ordre aux lecteurs d'écran (invisible à l'œil) */}
          <div aria-live="polite" className="sr-only">
            {announcement}
          </div>

          {/* Navigation entre les pages */}
          <div className="flex justify-between gap-3 sm:gap-4 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-10 text-sm sm:text-base"
              onClick={() => goToPage(currentPage - 1, form.getValues())}
              disabled={currentPage === 0 || isNavigating}
            >
              ← Précédent
            </Button>

            {isFinalQuestionsPage ? (
              <Button
                type="submit"
                size="lg"
                className="h-11 text-sm sm:text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={
                  !editable ||
                  form.formState.isSubmitting ||
                  isNavigating ||
                  !questionsFinalesComplete
                }
              >
                {form.formState.isSubmitting
                  ? "Envoi en cours..."
                  : "✓ Valider mes réponses"}
              </Button>
            ) : (
              <Button
                type="button"
                className="h-11 sm:h-10 text-sm sm:text-base"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  void goToPage(currentPage + 1, form.getValues());
                }}
                disabled={isNavigating}
              >
                {isNavigating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  "Suivant →"
                )}
              </Button>
            )}
          </div>

          <FormTransitionOverlay open={showTransitionOverlay} />
        </form>
      </Form>
    </div>
  );
}
